import { useCallback } from "react";
import type {
  Airport,
  FlightItinerarySegment,
} from "@b2bportal/air-booking-api";
import { DisruptionExerciseSelection } from "@b2bportal/core-disruption";
import { useDisruptionStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreDisruptionComponent } from "@b2bportal/core-types";
import { AirlineIcon } from "@b2bportal/core-ui";
import { I18nMarkup, useI18nContext } from "@hopper-b2b/i18n";
import { DateTimeFormatStyle, formatDateTime } from "@hopper-i18n/formatter";
import dayjs from "dayjs";
import defaultStyles from "./DisruptionExerciseFlightSliceCard.module.scss";

interface DisruptionExerciseFlightSliceCardProps {
  slice: { segments: Array<FlightItinerarySegment> };
  isSelected: boolean;
  isReturn: boolean;
  onSelect?: () => void;
  airports: { [key: string]: Airport };
}

export const DisruptionExerciseFlightSliceCard = (
  props: DisruptionExerciseFlightSliceCardProps
) => {
  const { t, language, brand } = useI18nContext();
  const COMPONENT_KEY =
    CoreDisruptionComponent.DisruptionExerciseFlightSliceCard;
  const styles = useDisruptionStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const getCityName = (code: string) => {
    return props.airports[code]?.cityName || "";
  };

  const lastSegment = props.slice.segments[props.slice.segments.length - 1];
  const firstSegment = props.slice.segments[0];
  const destinationCode = lastSegment.destination.locationCode;
  const destination = getCityName(destinationCode);
  const date = formatDateTime(
    dayjs(firstSegment.updatedDeparture).toDate(),
    language,
    DateTimeFormatStyle.MediumDate
  );

  const headline = props.isReturn ? (
    <I18nMarkup
      tKey={"core-disruption.disruptionUniversalExercise.rebook.returnHeadline"}
      className={cn("regulerText")}
      replacements={{
        destination: destination,
        airport: destinationCode,
        date: date,
      }}
    />
  ) : (
    <I18nMarkup
      tKey={
        "core-disruption.disruptionUniversalExercise.rebook.outboundHeadline"
      }
      className={cn("regulerText")}
      replacements={{
        destination: destination,
        airport: destinationCode,
        date: date,
      }}
    />
  );

  const time = () => {
    const firstSegmentDepartureTime = dayjs(
      firstSegment.updatedDeparture
    ).format(brand.timeFormat);

    const lastSegmentArrivalTime = dayjs(firstSegment.updatedArrival).format(
      brand.timeFormat
    );

    const flightTime = t(
      "core-disruption.disruptionUniversalExercise.rebook.flightTime",
      {
        fromTime: firstSegmentDepartureTime,
        toTime: lastSegmentArrivalTime,
      }
    );

    return (
      <span className={cn("regulerText")}>
        <b>{flightTime}</b>
      </span>
    );
  };

  const duration = () => {
    const hours = dayjs(lastSegment.updatedArrival).diff(
      dayjs(firstSegment.updatedDeparture),
      "hours"
    );
    return (
      <span className={cn("regulerText")}>
        {t("core-disruption.disruptionUniversalExercise.hours", {
          numberOfHours: hours,
        })}
      </span>
    );
  };

  const stops = () => {
    const numberOfStops = props.slice.segments.length - 1;
    const hasConnections = numberOfStops > 0;
    const stops = t(
      "core-disruption.disruptionUniversalExercise.rebook.stops",
      {
        stops: numberOfStops,
      }
    );

    return hasConnections ? (
      <span className={cn("smallText")}>{stops}</span>
    ) : (
      <span className={cn("smallText")}>
        {t("core-disruption.disruptionUniversalExercise.rebook.direct")}
      </span>
    );
  };

  const airline = useCallback(() => {
    const airlineCodes = props.slice.segments
      .map((segment) => {
        return segment.operatingAirline?.code;
      })
      .filter(function (elem, index, self) {
        return index === self.indexOf(elem);
      });
    const code = airlineCodes[0];

    if (code != null) {
      return (
        <div className={cn("airlineContainer")}>
          <AirlineIcon airlineCode={code} size={"small"} />
          <span className={cn("smallText")}>{code}</span>
        </div>
      );
    } else {
      return <div></div>;
    }
  }, [props.slice.segments, cn]);

  return (
    <DisruptionExerciseSelection
      onSelect={props.onSelect}
      isSelected={props.isSelected}
      hideRadio={false}
      content={
        <div className={cn("disruptionExerciseFlightSliceCardContent")}>
          <div className={cn("topSection")}>{headline}</div>
          <div className={cn("bottomSection")}>
            <div className={cn("column")}>
              {time()}
              {airline()}
            </div>
            <div className={cn("verticalDivider")} />
            <div className={cn("column")}>
              {duration()}
              {stops()}
            </div>
          </div>
        </div>
      }
    />
  );
};

export default DisruptionExerciseFlightSliceCard;
